import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { HelpCircle, ChevronRight, Search, BookOpen, Users, User, Building } from "lucide-react";
import { manuals, moduleOrder, personaLabels } from "../help/manuals";

/**
 * Help / User Manual page.
 *
 * Layout: 3 tabs (Admin, Technician, Client) → search box → left rail with
 * modules → right panel with the rendered article for the selected module.
 *
 * The article is rendered from `manuals[persona][lang][moduleKey]`, keeping
 * copy separate from layout (see /app/frontend/src/help/*.js).
 */
export default function Help() {
  const { user } = useAuth();
  const { lang } = useLang();

  // Default the persona to the one that matches the logged-in role — techs
  // land on the Technician manual, clients on the Client manual, everyone
  // else on Admin (the most comprehensive).
  const defaultPersona = useMemo(() => {
    if (user?.role === "technician") return "technician";
    if (user?.role === "client") return "client";
    return "admin";
  }, [user]);

  const [persona, setPersona] = useState(defaultPersona);
  const [search, setSearch] = useState("");

  const activeModules = moduleOrder[persona] || [];
  const localized = manuals[persona]?.[lang] || {};
  const filteredKeys = search.trim()
    ? activeModules.filter((k) => {
        const article = localized[k];
        if (!article) return false;
        const haystack = [
          article.title, article.overview,
          ...(article.features || []),
          ...(article.tips || []),
        ].join(" ").toLowerCase();
        return haystack.includes(search.toLowerCase());
      })
    : activeModules;

  const [selectedKey, setSelectedKey] = useState(activeModules[0]);
  // Reset selection when persona changes
  React.useEffect(() => { setSelectedKey(moduleOrder[persona]?.[0]); }, [persona]);

  const article = localized[selectedKey];

  const personaTabs = [
    { key: "admin", icon: Users, testid: "help-tab-admin" },
    { key: "technician", icon: User, testid: "help-tab-technician" },
    { key: "client", icon: Building, testid: "help-tab-client" },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="help-page">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-slate-900" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              {lang === "pt" ? "Manual do Sistema" : "User Manual"}
            </h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {lang === "pt"
              ? "Guia completo por perfil e módulo. Alterne o idioma no rodapé do menu."
              : "Complete guide per persona and module. Switch language from the sidebar footer."}
          </p>
        </div>
      </div>

      {/* Persona tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 mb-4 flex-wrap" data-testid="help-persona-tabs">
        {personaTabs.map((p) => {
          const Icon = p.icon;
          const active = persona === p.key;
          return (
            <button
              key={p.key}
              data-testid={p.testid}
              onClick={() => setPersona(p.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-[2px] transition ${
                active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon className="w-4 h-4" />
              {personaLabels[lang][p.key]}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid="help-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === "pt" ? "Buscar em todos os módulos..." : "Search across all modules..."}
          className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Left rail — module list */}
        <aside className="md:col-span-1 border border-slate-200 rounded-xl bg-white p-2 h-fit max-h-[70vh] overflow-y-auto" data-testid="help-module-list">
          {filteredKeys.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">
              {lang === "pt" ? "Nada encontrado." : "Nothing found."}
            </div>
          ) : filteredKeys.map((k) => {
            const a = localized[k];
            if (!a) return null;
            const active = k === selectedKey;
            return (
              <button
                key={k}
                data-testid={`help-module-${k}`}
                onClick={() => setSelectedKey(k)}
                className={`w-full text-left px-3 py-2 text-sm rounded-lg mb-1 flex items-center justify-between gap-2 ${
                  active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <span className="truncate">{a.title}</span>
                <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-white" : "text-slate-400"}`} />
              </button>
            );
          })}
        </aside>

        {/* Right — article */}
        <article className="md:col-span-3 border border-slate-200 rounded-xl bg-white p-5 md:p-8" data-testid="help-article">
          {!article ? (
            <div className="text-sm text-slate-400 text-center py-16">
              <HelpCircle className="w-8 h-8 mx-auto mb-2" />
              {lang === "pt" ? "Selecione um módulo à esquerda." : "Pick a module on the left."}
            </div>
          ) : (
            <>
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                {personaLabels[lang][persona]}
              </div>
              <h2 className="text-2xl font-bold text-slate-900" data-testid="help-article-title">{article.title}</h2>
              {article.path && (
                <div className="mt-1 mb-4 inline-block px-2 py-0.5 rounded bg-slate-100 text-xs font-mono text-slate-600">
                  {article.path}
                </div>
              )}
              <p className="text-slate-700 leading-relaxed mb-6">{article.overview}</p>

              {article.features && article.features.length > 0 && (
                <section className="mb-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">
                    {lang === "pt" ? "Recursos" : "Key features"}
                  </h3>
                  <ul className="space-y-1.5">
                    {article.features.map((f, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-emerald-600 mt-0.5">✓</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {article.tasks && article.tasks.length > 0 && (
                <section className="mb-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
                    {lang === "pt" ? "Como fazer" : "How-to"}
                  </h3>
                  <div className="space-y-4">
                    {article.tasks.map((task, i) => (
                      <div key={i} className="border-l-4 border-slate-900 pl-4">
                        <div className="font-semibold text-slate-900 mb-1">{task.title}</div>
                        <ol className="space-y-1 list-decimal list-inside text-sm text-slate-700">
                          {task.steps.map((s, j) => (
                            <li key={j}>{s}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {article.tips && article.tips.length > 0 && (
                <section className="mb-6 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-amber-800 mb-2">
                    {lang === "pt" ? "Dicas" : "Tips"}
                  </h3>
                  <ul className="space-y-1.5">
                    {article.tips.map((t, i) => (
                      <li key={i} className="text-sm text-amber-900 flex gap-2">
                        <span className="mt-0.5">💡</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {article.troubleshooting && article.troubleshooting.length > 0 && (
                <section className="mb-2 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">
                    {lang === "pt" ? "Solução de problemas" : "Troubleshooting"}
                  </h3>
                  <ul className="space-y-1.5">
                    {article.troubleshooting.map((t, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-rose-500 mt-0.5">⚠</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </article>
      </div>
    </div>
  );
}
